import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StickyNote, X, Search, Send, Loader2, Trash2 } from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import axiosInstance from '../api/axiosInstance';
import API_ENDPOINTS from '../api/endpoints';
import styles from './AgentNotesWidget.module.css';

const getDateLabel = (dateStr) => {
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr);
  if (isToday(d)) return 'Today';
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'MMM d, yyyy');
};

const groupByDate = (notes) => {
  const groups = {};
  notes.forEach((note) => {
    const label = getDateLabel(note.createdAt);
    if (!groups[label]) groups[label] = [];
    groups[label].push(note);
  });
  return groups;
};

const AgentNotesWidget = () => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const feedRef = useRef(null);
  const bottomRef = useRef(null);

  const fetchNotes = useCallback(async (pg = 1, search = '') => {
    if (pg === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = { page: pg, limit: 10 };
      if (search.trim()) params.search = search.trim();
      const res = await axiosInstance.get(API_ENDPOINTS.agentNotes, { params: params });
      const d = res.data?.response?.[0]?.data;
      const fetched = d?.notes || [];
      if (pg === 1) {
        setNotes(fetched);
      } else {
        setNotes((prev) => {
          const ids = new Set(prev.map((n) => n._id));
          return [...prev, ...fetched.filter((n) => !ids.has(n._id))];
        });
      }
      setHasMore(pg < (d?.pages || 1));
    } catch { /* ignore */ }
    setLoading(false);
    setLoadingMore(false);
  }, []);

  useEffect(() => {
    if (open) {
      setPage(1);
      fetchNotes(1, searchQuery);
    }
  }, [open, fetchNotes]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      setPage(1);
      fetchNotes(1, searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchNotes, open]);

  const handleScroll = useCallback(() => {
    const el = feedRef.current;
    if (!el || loadingMore || !hasMore) return;
    if (el.scrollTop <= 60) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchNotes(nextPage, searchQuery);
    }
  }, [loadingMore, hasMore, page, searchQuery, fetchNotes]);

  const handleSend = async () => {
    if (!noteText.trim() || noteText.length > 1000) return;
    setSending(true);
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.agentNotes, { content: noteText.trim() });
      const newNote = res.data?.response?.[0]?.data;
      if (newNote) {
        setNotes((prev) => [newNote, ...prev]);
      }
      setNoteText('');
      setTimeout(() => {
        if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
      }, 100);
    } catch { /* ignore */ }
    setSending(false);
  };

  const handleDelete = async (noteId) => {
    try {
      await axiosInstance.delete(`${API_ENDPOINTS.agentNotes}/${noteId}`);
      setNotes((prev) => prev.filter((n) => n._id !== noteId));
    } catch { /* ignore */ }
  };

  const sortedAsc = [...notes].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const grouped = groupByDate(sortedAsc);

  return (
    <>
      {/* Collapsed pill */}
      {!open && (
        <button type="button" className={styles.pill} onClick={() => setOpen(true)} aria-expanded={false}>
          <StickyNote size={18} />
          <span>Agent Notes</span>
        </button>
      )}

      {/* Expanded panel */}
      {open && (
        <div className={styles.panel}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <StickyNote size={18} />
              <span>Agent Notes</span>
            </div>
            <button type="button" className={styles.closeBtn} onClick={() => setOpen(false)} aria-label="Close notes" title="Close">
              <X size={18} />
            </button>
          </div>

          {/* Search */}
          <div className={styles.searchBar}>
            <Search size={14} />
            <input
              type="text"
              placeholder="Search notes..."
              aria-label="Search notes"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button type="button" onClick={() => setSearchQuery('')} className={styles.searchClear} aria-label="Clear search">
                <X size={12} />
              </button>
            )}
          </div>

          {/* Chat feed */}
          <div className={styles.feed} ref={feedRef} onScroll={handleScroll}>
            {loadingMore && (
              <div className={styles.loadingMore}><Loader2 size={16} className={styles.spin} /></div>
            )}
            {loading ? (
              <div className={styles.emptyState}><Loader2 size={28} className={styles.spin} /><span>Loading...</span></div>
            ) : sortedAsc.length === 0 ? (
              <div className={styles.emptyState}><StickyNote size={36} /><span>No notes yet</span></div>
            ) : (
              Object.entries(grouped).map(([dateLabel, items]) => (
                <div key={dateLabel}>
                  <div className={styles.dateChip}><span>{dateLabel}</span></div>
                  {items.map((note) => (
                    <div key={note._id} className={styles.bubble}>
                      <div className={styles.bubbleContent}>{note.content}</div>
                      <div className={styles.bubbleMeta}>
                        <span>{note.createdAt ? format(new Date(note.createdAt), 'h:mm a') : ''}</span>
                        <button
                          type="button"
                          className={styles.deleteBtn}
                          onClick={() => handleDelete(note._id)}
                          title="Delete note"
                          aria-label="Delete note"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
            <div ref={bottomRef} />
          </div>

          {/* Composer */}
          <div className={styles.composer}>
            <textarea
              placeholder="Type a note..."
              aria-label="New note"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value.slice(0, 1000))}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              rows={2}
            />
            <div className={styles.composerFooter}>
              <span className={styles.charCount}>{noteText.length} / 1000</span>
              <button
                type="button"
                className={styles.sendBtn}
                onClick={handleSend}
                disabled={!noteText.trim() || sending}
                aria-label="Send note"
                title="Send"
              >
                {sending ? <Loader2 size={16} className={styles.spin} /> : <Send size={16} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AgentNotesWidget;
